import {
  Button,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Alert,
  Box,
  MenuItem,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { StepContainer } from "../StepContainer";
import { Depot } from "../stateMachine/types/depots";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const TwoFieldsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-evenly;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export interface Props {
  handleNext: (name: string, comment?: string) => void;
  handleNewDepot: (name: string, comment?: string) => void;
  handlePrevious: () => void;
  defaultName: string;
  defaultComment?: string;
  selectedDepotId?: number;
  handleDepotIdChange: (id: number) => void;
  selectableDepots: Depot[];
  existingLots: number;
}

export const NameAndDepotStep = ({
  handleNext,
  handleNewDepot,
  handlePrevious,
  defaultName,
  defaultComment,
  selectedDepotId,
  handleDepotIdChange,
  selectableDepots,
  existingLots,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [name, setName] = useState<string>(defaultName);
  const [comment, setComment] = useState<string>(defaultComment || "");

  const onNextDisabled = !name?.trim() || !selectedDepotId;

  return (
    <StepContainer
      testid="name-and-depot-step"
      title={t("transactionWizard.nameAndDepotStep.title")}
      onNext={() => {
        if (onNextDisabled) return;
        handleNext(name, comment);
      }}
      onNextDisabled={onNextDisabled}
      onPrevious={handlePrevious}
    >
      <Container>
        <Box>
          <TwoFieldsContainer>
            <FormControl fullWidth>
              <InputLabel>
                {t("transactionWizard.nameAndDepotStep.selectDepot")}
              </InputLabel>
              <Select
                data-testid="select-depot"
                displayEmpty
                value={selectedDepotId}
                onChange={(event) => {
                  handleDepotIdChange(parseInt(event.target.value as string));
                }}
                inputProps={{ "data-testid": "select-depot-input" }}
              >
                {!selectableDepots.length && (
                  <MenuItem aria-label="None" value="" disabled>
                    None
                  </MenuItem>
                )}
                {selectableDepots.map((depot, ix) => {
                  return (
                    <MenuItem
                      key={depot.id}
                      data-testid={`select-depot-${ix}`}
                      value={depot.id}
                    >
                      {depot.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button
              variant="primary"
              size="small"
              data-testid="new-depot"
              onClick={() => handleNewDepot(name, comment)}
              sx={{ flexShrink: 0 }}
            >
              {t("transactionWizard.nameAndDepotStep.newDepot")}
            </Button>
          </TwoFieldsContainer>

          {existingLots > 0 && (
            <Alert
              severity="info"
              data-testid="investment-exists-info"
              sx={{ marginTop: theme.spacing(2) }}
            >
              {t("transactionWizard.nameAndDepotStep.investmentExists", {
                lots: existingLots,
              })}
            </Alert>
          )}
        </Box>

        <Box>
          <TextField
            autoComplete="off"
            label={t("transactionWizard.nameAndDepotStep.name")}
            data-testid="name"
            fullWidth
            onChange={(event) => {
              setName(event.target.value);
            }}
            type="text"
            value={name}
            slotProps={{
              htmlInput: {
                style: { textAlign: "left" },
                "data-testid": "name-input",
              },
            }}
          />
        </Box>

        <TextField
          autoComplete="off"
          label={t("transactionWizard.nameAndDepotStep.comment")}
          data-testid="comment"
          fullWidth
          rows={5}
          multiline
          onChange={(event) => {
            setComment(event.target.value);
          }}
          type="text"
          value={comment}
          slotProps={{
            htmlInput: {
              style: { textAlign: "left" },
              "data-testid": "comment-input",
            },
          }}
        />
      </Container>
    </StepContainer>
  );
};
